import axios from "http/customAxios";
import {
  addDaysToStringDate,
  formatDefaultDate,
  isAfterDates,
  isBeforeDates,
  subDaysToStringDate,
} from "../helpers/dates";
import { DailySet, ValueTypes } from "../shared/types";
import { ARIData } from "../shared/domain/ARIData";
import { InventoryData } from "../shared/domain/InventoryData";
import { ExtraData } from "../shared/domain/ExtraData";
import { PromotionData } from "../shared/domain/PromotionData";
import { addDays, differenceInDays, getDay } from "date-fns";
import { AriType } from "../updateForm";
import { BundleInventoryData } from "../shared/domain/BundleInventoryData";
import { ComplementData } from "../shared/domain/ComplementData";

export const fetchBackofficeARI = (
  propertyTicker: string,
  startDate: string,
  endDate: string,
  url: string
) => {
  // Add 1 day to endDate because some ARI values are returned with endDate not included in their
  // dailySet, for the other ARI values that added day will be removed
  const finalEndDate = addDaysToStringDate(endDate, 1);
  return axios.post(
    `${url}/middleware-adapter/establishment/${propertyTicker}/backofficeARI` +
      `?startDate=${startDate}&endDate=${finalEndDate}&onlyInventories=false`,
    {}
  );
};

export const updateSingleARIValue = (
  propertyTicker: string,
  url: string,
  ticker: string,
  valueType: ValueTypes,
  date: string,
  value: number | boolean,
  type: AriType,
  accessToken
) => {
  return axios.post(
    `${url}/middleware-adapter/establishment/${propertyTicker}/updateARIValues`,
    {
      [ticker]: {
        type,
        values: {
          [valueType]: {
            dailySet: [
              {
                startDate: date,
                endDate: date,
                value,
                operator: "=",
              },
            ],
          },
        },
      },
    },
    {
      headers: {
        Authorization: accessToken,
      },
    }
  );
};

const getFilteredPeriodsByWeekday = (
  periods: {
    startDate: Date;
    endDate: Date;
    weekdays: number[];
  }[],
  operator: string,
  value: number | boolean
) => {
  const result: {
    startDate: string;
    endDate: string;
    value: number | boolean;
    operator: string;
  }[] = [];
  periods.forEach(it => {
    if (it.weekdays.length === 7) {
      result.push({
        startDate: formatDefaultDate(it.startDate),
        endDate: formatDefaultDate(it.endDate),
        value,
        operator,
      });
      return;
    }

    let daysList: { date: Date; valid: boolean }[] = [];
    const days = differenceInDays(new Date(it.endDate), new Date(it.startDate));
    let auxDate = it.startDate;
    Array.from({ length: days }, () => {
      if (!it.weekdays.includes(getDay(new Date(auxDate)))) {
        daysList.push({
          date: auxDate,
          valid: false,
        });
      } else {
        daysList.push({
          date: auxDate,
          valid: true,
        });
      }
      auxDate = addDays(auxDate, 1);
      return daysList;
    });
    daysList = daysList.filter(it => it.valid);
    auxDate = daysList[0].date;
    daysList.forEach((it, index) => {
      if (daysList[index + 1] && differenceInDays(it.date, daysList[index + 1].date) < -1) {
        result.push({
          startDate: formatDefaultDate(auxDate),
          endDate: formatDefaultDate(it.date),
          value,
          operator,
        });
        auxDate = daysList[index + 1].date;
      }
      if (index === daysList.length - 1) {
        result.push({
          startDate: formatDefaultDate(auxDate),
          endDate: formatDefaultDate(it.date),
          value,
          operator,
        });
      }
    });
  });
  return result;
};

export const updateARIValues = (
  propertyTicker: string,
  url: string,
  valueType: ValueTypes,
  operator: string,
  value: number | boolean,
  periods: {
    startDate: Date;
    endDate: Date;
    weekdays: number[];
  }[],
  ariItems: string[],
  type: AriType,
  accessToken
) => {
  const data = ariItems.reduce(
    (previousValue, currentValue) => ({
      [currentValue]: {
        type,
        values: {
          [valueType]: {
            dailySet: getFilteredPeriodsByWeekday(periods, operator, value),
          },
        },
      },
      ...previousValue,
    }),
    {}
  );

  return axios.post(
    `${url}/middleware-adapter/establishment/${propertyTicker}/updateARIValues`,
    data,
    {
      headers: {
        Authorization: accessToken,
      },
    }
  );
};

const getDefaultValueByType = (valueType: string): number | boolean | string => {
  switch (valueType) {
    case ValueTypes.closed:
    case ValueTypes.closeToArrival:
    case ValueTypes.closeToDeparture:
      return false;

    default:
      return 0;
  }
};

function fillDateGaps(dateArray: DailySet[], defaultValue: number | boolean | string): DailySet[] {
  if (!dateArray.length) {
    return [];
  }

  const filledArray: DailySet[] = [];
  filledArray.push(dateArray[0]);
  for (let i = 1; i < dateArray.length; i++) {
    const currentDate = dateArray[i].startDate;
    const previousDate = dateArray[i - 1].endDate;

    if (isAfterDates(currentDate, previousDate)) {
      filledArray.push({
        startDate: addDaysToStringDate(previousDate, 1),
        endDate: subDaysToStringDate(currentDate, 1),
        value: defaultValue,
      });
    }
    filledArray.push(dateArray[i]);
  }

  return filledArray;
}

const getFixedDailySet = (
  dailySet: DailySet[],
  startDate: string,
  endDate: string,
  defaultValue: number | boolean | string
): DailySet[] => {
  if (!dailySet || !dailySet.length) {
    return [{ startDate, endDate, value: defaultValue }];
  }

  let resultDailySet = [...dailySet];

  // Add first dailySet if there is no period for startDate
  if (isAfterDates(resultDailySet[0].startDate, startDate)) {
    resultDailySet.splice(0, 0, {
      startDate,
      endDate: subDaysToStringDate(resultDailySet[0].startDate, 1),
      value: defaultValue,
    });
  }

  // Add dailySets for periods without values
  resultDailySet = fillDateGaps(resultDailySet, defaultValue);

  if (isBeforeDates(endDate, resultDailySet[resultDailySet.length - 1].endDate)) {
    resultDailySet[resultDailySet.length - 1].endDate = endDate;
  }

  // Add last dailySet if there is no period for endDate
  if (isBeforeDates(resultDailySet[resultDailySet.length - 1].endDate, endDate)) {
    resultDailySet.push({
      startDate: addDaysToStringDate(resultDailySet[resultDailySet.length - 1].endDate, 1),
      endDate,
      value: defaultValue,
    });
  }

  return resultDailySet;
};

const getDailyValues = (
  ariValues: {
    ticker: string;
    dailyValues: {
      [key in ValueTypes]?: {
        dailySet?: DailySet[];
        parentTicker?: string;
      };
    };
  }[],
  startDate: string,
  endDate: string
) =>
  ariValues.map(it => {
    const dailyValues = Object.entries(it.dailyValues).reduce((acc, [valueType, values]) => {
      if (!values) {
        return {};
      }
      let value = getDefaultValueByType(valueType);
      if (it instanceof ExtraData && valueType === ValueTypes.availability) {
        value = "-";
      }
      if (!values.dailySet || !values.dailySet.length) {
        return {
          ...acc,
          [valueType]: {
            dailySet: [{ startDate, endDate, value }],
            parentTicker: values.parentTicker,
          },
        };
      }
      return {
        ...acc,
        [valueType]: {
          ...values,
          dailySet: getFixedDailySet(
            values.dailySet,
            startDate,
            endDate,
            value
          ),
          parentTicker: values.parentTicker,
        },
      };
    }, {});

    return {
      ...it,
      dailyValues,
    };
  });

export const processARIData = (startDate: string, endDate: string, ariData: ARIData): ARIData => {
  const { inventories, promos, extras, complements, bundleInventories } = ariData;
  if (!inventories || !promos || !extras || !complements || !bundleInventories) {
    return {};
  }

  return {
    inventories: getDailyValues(inventories, startDate, endDate) as InventoryData[],
    promos: getDailyValues(promos, startDate, endDate) as PromotionData[],
    extras: getDailyValues(extras, startDate, endDate) as ExtraData[],
    complements: complements.map(it => ({
      ticker: it.ticker,
      dailySet: getFixedDailySet(
        it.dailySet,
        startDate,
        endDate,
        getDefaultValueByType(ValueTypes.rate)
      ),
    })),
    bundleInventories: getDailyValues(
      bundleInventories,
      startDate,
      endDate
    ) as BundleInventoryData[],
  };
};
